import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { Alert } from '../../../../../../shared/lib/common/Alert';
import { CustomController } from '../../../../../../shared/lib/common/CustomController';
import { PageHeading } from '../../../../../../shared/lib/common/PageHeading';
import { BUTTON_TEXT_GET_STARTED, BUTTON_TEXT_LOGIN, FORGET_PASSWORD, GRAPHQL_QUERY_POLICY, PAGE_SUBTITLE_LOGIN, PAGE_TITLE_LOGIN_PORTAL, TEXT_DONT_HAVE_ACCOUNT } from '../../../../../../shared/lib/constants';
import { withTrademark } from '../../../../../../shared/lib/common/Tokenizer';
import { LoginType, LoginUserInput, useGetUserLazyQuery, useLoginMutation } from '../../../../generated';
import palette from '../../../../../../shared/lib/theme/palette';
import { authBackdropStyle, authCardStyle, authCardWrapper, authHeadingCentered, authInlineLinkStyle, authLinkStyle, forgotPasswordRow } from '../../../../../../shared/lib/theme/styleConstants';
import { AuthContainer } from '../../../../components/auth/AuthContainer';
import { AUTH_TOKEN, EMAIL_CHANGED_OR_NOT_VERIFIED_PLEASE_VERIFY_EMAIL, INCORRECT_EMAIL_OR_PASSWORD, LINKS, LOGIN_FIELDS } from '../../../constants';
import { useAppContext } from '../../../contexts/AppContext';
import { loginValidationSchema } from '../../../validationSchema';
import { useEffect } from 'react';
import PendingPromoAlert from '../../../../components/common/PendingPromoAlert';
import { useMobileView } from '../../../hooks/useMobileView';

const SignIn = () => {
  const { setIsAppUserLoggedIn, setCurrentUser, isAppUserLoggedIn, currentUser } = useAppContext();
  const navigate = useNavigate();
  const { blackOne } = palette;
  const mediumViewport = useMobileView();

  const methods = useForm<LoginUserInput, any, LoginUserInput>({
    mode: 'all',
    resolver: yupResolver(loginValidationSchema),

    defaultValues: {
      email: '',
      password: '',
      rememberMe: false
    }
  });

  const {
    handleSubmit,
    formState: { isValid, isSubmitting }
  } = methods;

  const onSubmit: SubmitHandler<LoginUserInput> = async (data) => {
    const { email, password, rememberMe } = data || {};

    await login({
      variables: {
        loginUser: {
          email,
          password,
          rememberMe,
          loginType: LoginType.Portal
        }
      }
    });
  };

  const [fetchUser, { loading: userLoading }] = useGetUserLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError() {
      setCurrentUser(null);
    },

    onCompleted(data) {
      if (data) {
        const { me } = data;

        if (me) {
          setCurrentUser(me);
        }
      }
    }
  });

  const [login, { loading }] = useLoginMutation({
    onError({ message }) {
      if (message === 'ApolloError: Incorrect Email or Password') Alert.error(INCORRECT_EMAIL_OR_PASSWORD);
      else if (message === EMAIL_CHANGED_OR_NOT_VERIFIED_PLEASE_VERIFY_EMAIL) Alert.error(EMAIL_CHANGED_OR_NOT_VERIFIED_PLEASE_VERIFY_EMAIL);
      else Alert.error(message);
      setIsAppUserLoggedIn(false);
    },

    async onCompleted(data) {
      setIsAppUserLoggedIn(false);
      const {
        login: { accessToken }
      } = data;

      if (accessToken) {
        localStorage.setItem(AUTH_TOKEN, accessToken);
        setIsAppUserLoggedIn(true);

        await fetchUser();

        if (currentUser) {
          const { isSubscribed } = currentUser;
          const { customerStripeId, subscriptionCancelled } = isSubscribed || {};

          if (customerStripeId || subscriptionCancelled) {
            navigate(LINKS.DASHBOARD_LINK);
          } else {
            navigate(LINKS.ONBOARDING_LINK);
          }
        }
      }
    }
  });

  useEffect(() => {
    const localToken = localStorage.getItem(AUTH_TOKEN);

    if (localToken && isAppUserLoggedIn && currentUser) {
      const { isSubscribed } = currentUser;
      const { customerStripeId, subscriptionCancelled } = isSubscribed || {};

      if (customerStripeId || subscriptionCancelled) {
        navigate(LINKS.DASHBOARD_LINK);
      } else {
        navigate(LINKS.ONBOARDING_LINK);
      }
    }
  }, [isAppUserLoggedIn]);

  return (
    <AuthContainer>
      <Box sx={authBackdropStyle} />

      <Box flex={mediumViewport ? 'none' : 1} sx={authCardWrapper}>
        <Box sx={authCardStyle}>
          <PendingPromoAlert />

          <Box sx={authHeadingCentered}>
            <PageHeading color={blackOne} title={withTrademark(PAGE_TITLE_LOGIN_PORTAL)} subTitle={PAGE_SUBTITLE_LOGIN} />
          </Box>

          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {LOGIN_FIELDS.map((field) => {
                const { fieldType, name, title } = field;

                return (
                  <Box mb={2} key={name}>
                    <CustomController key={`${title}-${name}`} controllerName={name} controllerLabel={title} fieldType={fieldType} />
                  </Box>
                );
              })}

              <Box mb={2} sx={forgotPasswordRow}>
                <Link to={LINKS.FORGOT_PASSWORD_LINK}>
                  <Typography sx={authLinkStyle} variant="subtitle2">
                    {FORGET_PASSWORD}
                  </Typography>
                </Link>
              </Box>

              <Button
                variant="contained"
                type="submit"
                fullWidth
                disabled={loading || userLoading || !isValid || isSubmitting}
                endIcon={(loading || userLoading) && <CircularProgress size={20} color="inherit" />}
              >
                {BUTTON_TEXT_LOGIN}
              </Button>
            </form>
          </FormProvider>

          <Box display="flex" justifyContent="center" mt={2}>
            <Typography sx={{ color: blackOne }} display="flex" alignItems="center" variant="subtitle2">
              {TEXT_DONT_HAVE_ACCOUNT}
              <Link to={LINKS.REGISTER_LINK}>
                <Typography variant="subtitle2" sx={authInlineLinkStyle}>
                  {BUTTON_TEXT_GET_STARTED}
                </Typography>
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </AuthContainer>
  );
};

export default SignIn;
